import { rgba } from 'polished'
import { standaloneMedia } from 'style/standaloneMedia'
import styled, { css } from 'styled-components'

export const AppWrapper = styled.div`
  ${({
    theme: {
      app: { appSecondaryColor, workspaceGlow, workspaceLine },
    },
  }) => css`
    height: 100%;
    background-color: ${appSecondaryColor};
    background-image: radial-gradient(circle at 14% 12%, ${workspaceGlow} 0, transparent 32%),
      radial-gradient(circle at 88% 86%, ${workspaceGlow} 0, transparent 28%),
      linear-gradient(90deg, ${workspaceLine} 1px, transparent 1px),
      linear-gradient(${workspaceLine} 1px, transparent 1px);
    background-size: auto, auto, 32px 32px, 32px 32px;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 72px 1fr;
    grid-template-areas:
      'head'
      'content';

    ${standaloneMedia(css`
      grid-template-columns: 1fr;
      grid-template-rows: 72px 1fr;
      height: 100vh;
    `)}
  `}
`

export const CenteredGrid = styled.div`
  grid-area: content;
  display: grid;
  place-items: center;

  ${standaloneMedia(css`
    height: calc(100vh - 72px);
    width: 100%;
  `)}
`

export const AppHeader = styled.div`
  ${({
    theme: {
      app: { headerGradient, headerShadow },
    },
  }) => css`
    background: ${headerGradient};
    color: #fff;
    grid-area: head;
    display: grid;
    align-items: center;
    grid-template-columns: max-content minmax(0, 1fr) max-content;
    gap: 18px;
    box-shadow: ${headerShadow};
    padding: 0 20px;
    position: relative;
    isolation: isolate;
    z-index: 10;

    ::after {
      content: '';
      position: absolute;
      inset: auto 0 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(120, 244, 229, 0.55), transparent);
      pointer-events: none;
    }

    ${standaloneMedia(css`
      grid-template-columns: max-content minmax(0, 1fr) max-content;
      align-items: center;
      padding: 0 10px;
      position: fixed;
      width: 100%;
      height: 72px;
    `)}

    @media (max-width: 700px) {
      gap: 8px;
      padding: 0 8px;
    }
  `}
`

export const HeaderBrand = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 12px;

  img {
    width: 46px;
    height: 46px;
    border-radius: 14px;
    flex: 0 0 auto;
    border: 1px solid rgba(255, 255, 255, 0.32);
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.28), 0 0 24px rgba(53, 208, 186, 0.16);
  }

  @media (max-width: 1040px) {
    .brand-meta {
      display: none;
    }
  }

  @media (max-width: 900px) {
    .brand-copy {
      display: none;
    }

    img {
      width: 40px;
      height: 40px;
      border-radius: 12px;
    }
  }
`

export const BrandCopy = styled.div`
  min-width: 0;
  line-height: 1;
`

export const BrandTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.2px;
`

export const BrandMeta = styled.div.attrs({ className: 'brand-meta' })`
  margin-top: 7px;
  display: flex;
  align-items: center;
  gap: 7px;
  color: rgba(225, 251, 252, 0.78);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.65px;
  text-transform: uppercase;

  .brand-dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #52d8c6;
    box-shadow: 0 0 8px rgba(82, 216, 198, 0.8);
  }
`

export const HeaderActions = styled.div`
  justify-self: center;
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;

  .header-action {
    color: #fff;
    height: 40px;
    border-color: rgba(217, 251, 250, 0.26);
    border-radius: 13px;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(12px);
    text-transform: none;
    white-space: nowrap;
    font-weight: 600;
    padding: 0 14px;
    transition: transform 0.18s ease, background 0.18s ease, border-color 0.18s ease;

    :hover {
      border-color: rgba(133, 244, 229, 0.72);
      background: rgba(82, 216, 198, 0.15);
      transform: translateY(-1px);
    }
  }

  @media (max-width: 700px) {
    gap: 4px;

    .header-action {
      min-width: 36px;
      width: 36px;
      height: 36px;
      padding: 0;
      border-radius: 50%;

      .MuiButton-startIcon {
        margin: 0;
      }

      .action-label {
        display: none;
      }
    }
  }
`

export const HeaderUtilities = styled.div`
  justify-self: end;
  display: grid;
  grid-template-columns: repeat(3, max-content);
  gap: 5px;
  padding: 5px;
  border-radius: 16px;
  border: 1px solid rgba(220, 252, 252, 0.12);
  background: rgba(1, 9, 20, 0.16);

  @media (max-width: 700px) {
    gap: 5px;
  }
`

export const TorrentListWrapper = styled.div`
  grid-area: content;
  padding: 20px;
  overflow: auto;

  display: grid;
  place-content: start;
  grid-template-columns: repeat(auto-fit, minmax(450px, 570px));
  gap: 20px;

  @media (max-width: 1260px), (max-height: 500px) {
    padding: 10px;
    gap: 15px;
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }

  ${standaloneMedia(css`
    height: calc(100vh - 72px);
    padding-bottom: 24px;
  `)}
`

export const HeaderToggle = styled.button`
  ${({
    theme: {
      app: { headerToggleColor },
    },
  }) => css`
    cursor: pointer;
    border-radius: 11px;
    border: 1px solid transparent;
    background: transparent;
    height: 35px;
    width: 35px;
    transition: all 0.2s;
    font-weight: 600;
    display: grid;
    place-items: center;
    color: #fff;
    padding: 0;

    :hover {
      background: ${rgba(headerToggleColor, 0.86)};
      border-color: rgba(151, 240, 229, 0.25);
      transform: translateY(-1px);
    }

    :focus-visible {
      outline: 2px solid #77e9dc;
      outline-offset: 2px;
    }

    @media (max-width: 700px) {
      height: 28px;
      width: 28px;
      font-size: 12px;

      svg {
        width: 17px;
      }
    }
  `}
`
